<template>
  <div>
    <v-card class="mb-4">
      <v-card-title>
        <v-icon color="primary">mdi-file-document-outline</v-icon>
        <span class="px-2">{{ $t('Private Car Requests') }}</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="4">
            <v-select
              v-model="filterType"
              :items="requestTypeItems"
              :label="$t('request_type')"
              @change="loadRequests"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="filterStatus"
              :items="statusItems"
              :label="$t('status')"
              @change="loadRequests"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex align-center">
            <v-text-field
              v-model="search"
              :label="$t('search')"
              append-icon="mdi-magnify"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="requests"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:item.user="{ item }">
          <div v-if="item.user">
            <div class="font-weight-bold">{{ item.user.name }}</div>
            <div class="text-caption text-muted">{{ item.user.email }}</div>
          </div>
          <span v-else class="text-muted">{{ $t('unknown_user') }}</span>
        </template>
        <template v-slot:item.request_type="{ item }">
          <v-chip small dark :color="item.request_type === 'private_car' ? 'primary' : 'purple'">
            {{ item.request_type === 'private_car' ? $t('private_car') : $t('contract') }}
          </v-chip>
        </template>
        <template v-slot:item.car_details="{ item }">
          <div v-if="item.car">
            {{ item.car.name_en }} ({{ item.car.model }} {{ item.car.year }})
          </div>
          <div v-else-if="item.car_type">
            {{ item.car_type.name_en }}
          </div>
          <span v-else class="text-muted">-</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark small>
            {{ $t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.payment_status="{ item }">
          <v-chip :color="item.payment_status === 'paid' ? 'success' : 'grey'" dark small>
            {{ $t(item.payment_status) }}
          </v-chip>
        </template>
        <template v-slot:item.admin_price="{ item }">
          <span v-if="item.admin_price">{{ item.admin_price }}</span>
          <span v-else class="text-muted text-caption">-</span>
        </template>
        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn color="primary" small outlined @click="viewDetails(item)">
            {{ $t('view_details') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="detailsDialog" persistent max-width="800px">
      <v-card v-if="selectedItem">
        <v-card-title class="primary white--text">
          <span>{{ $t('view_details') }} - #{{ selectedItem.id }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <!-- Shared fields -->
            <v-col cols="12" sm="6">
              <label class="font-weight-bold d-block">{{ $t('Customer') }}:</label>
              <span>{{ selectedItem.user ? selectedItem.user.name : '-' }} ({{ selectedItem.user ? selectedItem.user.tel_number : '-' }})</span>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="font-weight-bold d-block">{{ $t('request_type') }}:</label>
              <span>{{ selectedItem.request_type === 'private_car' ? $t('private_car') : $t('contract') }}</span>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedItem.car_type">
              <label class="font-weight-bold d-block">{{ $t('car_type') }}:</label>
              <span>{{ selectedItem.car_type.name_en }} / {{ selectedItem.car_type.name_ar }}</span>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedItem.car">
              <label class="font-weight-bold d-block">{{ $t('Cars') }}:</label>
              <span>{{ selectedItem.car.name_en }} ({{ selectedItem.car.model }} - {{ selectedItem.car.year }})</span>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="font-weight-bold d-block">{{ $t('status') }}:</label>
              <v-chip :color="getStatusColor(selectedItem.status)" dark small>{{ $t(selectedItem.status) }}</v-chip>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="font-weight-bold d-block">{{ $t('payment_status') }}:</label>
              <v-chip :color="selectedItem.payment_status === 'paid' ? 'success' : 'grey'" dark small>{{ $t(selectedItem.payment_status) }}</v-chip>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedItem.admin_price">
              <label class="font-weight-bold d-block">{{ $t('admin_price') }}:</label>
              <span>{{ selectedItem.admin_price }}</span>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedItem.paid_at">
              <label class="font-weight-bold d-block">{{ $t('paid_at') }}:</label>
              <span class="text-caption">{{ selectedItem.paid_at | moment("LLL") }}</span>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedItem.payment_reference">
              <label class="font-weight-bold d-block">{{ $t('transaction_id') }}:</label>
              <span>{{ selectedItem.payment_reference }}</span>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedItem.offer_sent_at">
              <label class="font-weight-bold d-block">{{ $t('offer_sent_at') }}:</label>
              <span class="text-caption">{{ selectedItem.offer_sent_at | moment("LLL") }}</span>
            </v-col>
            
            <v-col cols="12"><v-divider></v-divider></v-col>

            <!-- Private Car fields -->
            <template v-if="selectedItem.request_type === 'private_car'">
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('pickup_address') }}:</label>
                <span>{{ selectedItem.pickup_address }}</span>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('dropoff_address') }}:</label>
                <span>{{ selectedItem.dropoff_address }}</span>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="font-weight-bold d-block">{{ $t('trip_date') }}:</label>
                <span>{{ selectedItem.trip_date }}</span>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="font-weight-bold d-block">{{ $t('trip_type') }}:</label>
                <span>{{ selectedItem.trip_type === 'one_way' ? $t('one_way') : $t('round_trip') }}</span>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="font-weight-bold d-block">{{ $t('departure_time') }}:</label>
                <span>{{ selectedItem.departure_time }}</span>
              </v-col>
              <v-col cols="12" sm="4" v-if="selectedItem.trip_type === 'round_trip'">
                <label class="font-weight-bold d-block">{{ $t('return_date') || 'تاريخ العودة' }}:</label>
                <span>{{ selectedItem.return_date ? selectedItem.return_date : selectedItem.trip_date }}</span>
              </v-col>
              <v-col cols="12" sm="4" v-if="selectedItem.return_time">
                <label class="font-weight-bold d-block">{{ $t('return_time') }}:</label>
                <span>{{ selectedItem.return_time }}</span>
              </v-col>
              <v-col cols="12" v-if="selectedItem.customer_notes">
                <label class="font-weight-bold d-block">{{ $t('customer_notes') }}:</label>
                <div class="pa-2 bg-light rounded text-caption border">{{ selectedItem.customer_notes }}</div>
              </v-col>
            </template>

            <!-- Contract fields -->
            <template v-else>
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('company_name') }}:</label>
                <span>{{ selectedItem.company_name }}</span>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('contract_duration') }}:</label>
                <span>{{ selectedItem.contract_duration }}</span>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('contact_person_name') }}:</label>
                <span>{{ selectedItem.contact_person_name }}</span>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('contact_phone') }}:</label>
                <span>{{ selectedItem.contact_phone }}</span>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="font-weight-bold d-block">{{ $t('cars_count') }}:</label>
                <span>{{ selectedItem.cars_count }}</span>
              </v-col>
              <v-col cols="12" sm="6" v-if="selectedItem.service_area">
                <label class="font-weight-bold d-block">{{ $t('service_area') }}:</label>
                <span>{{ selectedItem.service_area }}</span>
              </v-col>
              <v-col cols="12" sm="6" v-if="selectedItem.contract_start_date">
                <label class="font-weight-bold d-block">{{ $t('contract_start_date') }}:</label>
                <span>{{ selectedItem.contract_start_date }}</span>
              </v-col>
              <v-col cols="12" v-if="selectedItem.contract_notes">
                <label class="font-weight-bold d-block">{{ $t('contract_notes') }}:</label>
                <div class="pa-2 bg-light rounded text-caption border">{{ selectedItem.contract_notes }}</div>
              </v-col>
            </template>

            <!-- Actions block -->
            <v-col cols="12"><v-divider></v-divider></v-col>

            <!-- Send Offer block -->
            <v-col cols="12" v-if="selectedItem.status === 'pending' || selectedItem.status === 'awaiting_payment'">
              <v-card outlined class="pa-4 bg-light">
                <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('send_offer') }}</div>
                <v-form ref="offerForm" v-model="offerValid">
                  <v-text-field
                    v-model="offerPrice"
                    type="number"
                    step="0.01"
                    :rules="[v => !!v || 'Price is required', v => v > 0 || 'Price must be positive']"
                    :label="$t('admin_price') + '*'"
                    required
                  ></v-text-field>
                  <v-textarea
                    v-model="offerNotes"
                    :label="$t('admin_notes')"
                    rows="3"
                  ></v-textarea>
                  <v-btn color="primary" @click="submitOffer" :disabled="!offerValid || isActionLoading">
                    {{ $t('send_offer') }}
                  </v-btn>
                </v-form>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            v-if="selectedItem.status === 'pending' || selectedItem.status === 'awaiting_payment'"
            color="error"
            outlined
            :disabled="isActionLoading"
            @click="rejectRequest"
          >
            {{ $t('rejected') }}
          </v-btn>
          <v-btn
            v-if="selectedItem.status === 'confirmed'"
            color="warning"
            outlined
            class="mx-2"
            :disabled="isActionLoading"
            @click="cancelRequest"
          >
            {{ $t('cancelled') }}
          </v-btn>
          <v-btn
            v-if="selectedItem.status === 'confirmed' && selectedItem.payment_status === 'paid'"
            color="success"
            :disabled="isActionLoading"
            @click="completeRequest"
          >
            {{ $t('completed') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="detailsDialog = false">{{ $t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      requests: [],
      isLoading: false,
      isActionLoading: false,
      search: "",
      filterType: "all",
      filterStatus: "all",
      detailsDialog: false,
      selectedItem: null,
      offerValid: true,
      offerPrice: "",
      offerNotes: "",
      headers: [
        { text: "id", value: "id", align: "start" },
        { text: "Customer", value: "user" },
        { text: "request_type", value: "request_type" },
        { text: "car_type", value: "car_details" },
        { text: "status", value: "status" },
        { text: "payment_status", value: "payment_status" },
        { text: "admin_price", value: "admin_price" },
        { text: "created_at", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      requestTypeItems: [
        { text: "All Types", value: "all" },
        { text: "Private Car", value: "private_car" },
        { text: "Contract", value: "contract" }
      ],
      statusItems: [
        { text: "All Statuses", value: "all" },
        { text: "Pending", value: "pending" },
        { text: "Offer Sent / Awaiting Payment", value: "awaiting_payment" },
        { text: "Confirmed / Paid", value: "confirmed" },
        { text: "Rejected", value: "rejected" },
        { text: "Cancelled", value: "cancelled" },
        { text: "Completed", value: "completed" }
      ]
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page")
      };
    }
  },
  mounted() {
    this.loadRequests();
  },
  methods: {
    loadRequests() {
      this.isLoading = true;
      let params = {};
      if (this.filterType !== "all") params.request_type = this.filterType;
      if (this.filterStatus !== "all") params.status = this.filterStatus;

      axios
        .get("/private-car-bookings/requests", { params })
        .then(response => {
          this.requests = response.data;
        })
        .catch(error => {
          this.$notify({
            title: this.$t("Error"),
            text: "Failed to load requests",
            type: "error"
          });
          console.error(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    viewDetails(item) {
      this.selectedItem = item;
      this.offerPrice = item.admin_price || "";
      this.offerNotes = item.admin_notes || "";
      this.detailsDialog = true;
    },
    getStatusColor(status) {
      switch (status) {
        case "pending": return "warning";
        case "awaiting_payment": return "info";
        case "confirmed": return "success";
        case "rejected": return "error";
        case "cancelled": return "grey";
        case "completed": return "success";
        default: return "grey";
      }
    },
    submitOffer() {
      if (this.$refs.offerForm.validate()) {
        this.isActionLoading = true;
        axios
          .post(`/private-car-bookings/requests/${this.selectedItem.id}/send-offer`, {
            admin_price: this.offerPrice,
            admin_notes: this.offerNotes
          })
          .then(response => {
            this.detailsDialog = false;
            this.loadRequests();
            this.$notify({
              title: this.$t("Success"),
              text: "Offer price sent to customer.",
              type: "success"
            });
          })
          .catch(error => {
            this.$swal(this.$t("Error"), "Failed to send offer", "error");
          })
          .then(() => {
            this.isActionLoading = false;
          });
      }
    },
    rejectRequest() {
      this.$swal
        .fire({
          title: "Reject Request",
          text: "Are you sure you want to reject this request?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel")
        })
        .then(result => {
          if (result.isConfirmed) {
            this.isActionLoading = true;
            axios
              .post(`/private-car-bookings/requests/${this.selectedItem.id}/reject`)
              .then(() => {
                this.detailsDialog = false;
                this.loadRequests();
                this.$notify({
                  title: this.$t("Success"),
                  text: "Request marked as rejected.",
                  type: "success"
                });
              })
              .catch(error => {
                this.$swal(this.$t("Error"), "Failed to reject request", "error");
              })
              .then(() => {
                this.isActionLoading = false;
              });
          }
        });
    },
    cancelRequest() {
      this.$swal
        .fire({
          title: "Cancel Request",
          text: "Are you sure you want to cancel this request?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel")
        })
        .then(result => {
          if (result.isConfirmed) {
            this.isActionLoading = true;
            axios
              .post(`/private-car-bookings/requests/${this.selectedItem.id}/cancel`)
              .then(() => {
                this.detailsDialog = false;
                this.loadRequests();
                this.$notify({
                  title: this.$t("Success"),
                  text: "Request marked as cancelled.",
                  type: "success"
                });
              })
              .catch(error => {
                this.$swal(this.$t("Error"), "Failed to cancel request", "error");
              })
              .then(() => {
                this.isActionLoading = false;
              });
          }
        });
    },
    completeRequest() {
      this.$swal
        .fire({
          title: "Complete Request",
          text: "Are you sure you want to mark this request as completed?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel")
        })
        .then(result => {
          if (result.isConfirmed) {
            this.isActionLoading = true;
            axios
              .post(`/private-car-bookings/requests/${this.selectedItem.id}/complete`)
              .then(() => {
                this.detailsDialog = false;
                this.loadRequests();
                this.$notify({
                  title: this.$t("Success"),
                  text: "Request completed.",
                  type: "success"
                });
              })
              .catch(error => {
                let msg = error.response?.data?.message || "Failed to complete request";
                this.$swal(this.$t("Error"), msg, "error");
              })
              .then(() => {
                this.isActionLoading = false;
              });
          }
        });
    }
  }
};
</script>
